import { Download, ShowChart } from "@mui/icons-material";
import { Box, IconButton, Paper, Tooltip, Typography, useTheme } from "@mui/material";
import { ChartsAxisContentProps } from "@mui/x-charts";
import { LineChart } from "@mui/x-charts/LineChart";
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/es";
import "dayjs/locale/pt";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MonitoringResponse } from "../../../types/model/monitoring";
import { VariableResponse } from "../../../types/model/variable";
import { exportSvg, sanitizeFilename } from "../../../utils";
import { CommonOverlayProps } from "@mui/x-charts/ChartsOverlay/ChartsOverlay";

dayjs.extend(localizedFormat);

interface MonitoringChartProps {
  monitoringRecordsData: MonitoringResponse[];
  variables: VariableResponse[];
  isChartMaximized: boolean;
  captureName: string;
  selectedVariableId: number | undefined;
}

interface ChartDataPoint {
  id: number;
  value: number;
  measuredAt: Date | null;
  flag: string | null | undefined;
  notes: string | null | undefined;
  [key: string]: any;
}

interface MonitoringAxisContentProps extends ChartsAxisContentProps {
  axisValue: number | Date | string | null;
  filteredData?: ChartDataPoint[];
  variableUnit?: string;
}

interface NoDataOverlayProps extends CommonOverlayProps {
  message?: string;
}

function NoDataOverlay({ message }: Readonly<NoDataOverlayProps>) {
  return (
    <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 14 }}>
      {message}
    </text>
  );
}

function MonitoringAxisContent({ axisValue, filteredData, variableUnit }: Readonly<MonitoringAxisContentProps>) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  
  if (!filteredData || axisValue === null) return null;

  let axisTime: number;
  if (axisValue instanceof Date) {
    axisTime = axisValue.getTime();
  } else if (typeof axisValue === "number") {
    axisTime = axisValue;
  } else {
    axisTime = dayjs(axisValue).valueOf();
  }

  const dataPoint = filteredData.find(d => d.measuredAt?.getTime() === axisTime);

  if (!dataPoint) return null;

  return (
    <Paper sx={{ p: 1.5, border: `1px solid ${theme.palette.divider}`, minWidth: 200, boxShadow: theme.shadows[3] }}>
      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
        {dataPoint.value} {variableUnit}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {dayjs(dataPoint.measuredAt).locale(i18n.language).format("LLL")}
      </Typography>
      {dataPoint.notes && (
        <Typography variant="caption" sx={{ mt: 0.5, display: "block", fontStyle: "italic" }}>
          <strong>{t("captures.capture.monitoring.notes")}:</strong> {dataPoint.notes}
        </Typography>
      )}
    </Paper>
  );
}

export default function MonitoringChart({
  monitoringRecordsData,
  variables,
  isChartMaximized,
  captureName,
  selectedVariableId,
}: Readonly<MonitoringChartProps>) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const chartRef = useRef<HTMLDivElement>(null);

  const filteredData = useMemo<ChartDataPoint[]>(() => {
    if (!selectedVariableId) return [];
    
    return monitoringRecordsData
      .filter((item) => !!item.measuredAt)
      .map((item) => ({
        id: item.id,
        value: item.value,
        measuredAt: new Date(item.measuredAt!),
        flag: item.flag,
        notes: item.notes,
      }))
      .sort((a, b) => {
        if (!a.measuredAt || !b.measuredAt) return 0;
        return a.measuredAt.getTime() - b.measuredAt.getTime();
      });
  }, [monitoringRecordsData, selectedVariableId]);

  const diffDays = useMemo(() => {
    if (filteredData.length < 2) return 0;
    const first = filteredData[0].measuredAt!.getTime();
    const lastItem = filteredData.at(-1);
    if (!lastItem?.measuredAt) return 0;
    return (lastItem.measuredAt.getTime() - first) / (1000 * 60 * 60 * 24);
  }, [filteredData]);

  const selectedVariable = variables.find(v => v.id === selectedVariableId);
  const variableName = selectedVariable?.name ?? "Value";
  const variableUnit = selectedVariable?.unit;
  const label = variableUnit ? `${variableName} (${variableUnit})` : variableName;

  const height = isChartMaximized ? 600 : 350;

  const singleRecord = filteredData.length === 1;
  const singleValue = filteredData[0]?.value ?? 0;
  const singleDate = filteredData[0]?.measuredAt;

  const handleExport = () => {
    const svg = chartRef.current?.querySelector("svg");
    if (svg) {
      const filename = `${sanitizeFilename(captureName)}_${sanitizeFilename(variableName)}`;
      exportSvg(svg, filename);
    }
  };

  if (variables.length === 0) return null;

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <Tooltip title={t("generic.export")}>
          <span>
            <IconButton onClick={handleExport} color="primary" disabled={!selectedVariableId || filteredData.length === 0}>
              <Download />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Box ref={chartRef}>
        {selectedVariableId ? (
          <LineChart
            dataset={filteredData}
            xAxis={[{
              dataKey: "measuredAt",
              scaleType: "time",
              min: singleRecord && singleDate ? new Date(singleDate.getTime() - 60 * 60 * 1000) : undefined,
              max: singleRecord && singleDate ? new Date(singleDate.getTime() + 60 * 60 * 1000) : undefined,
              valueFormatter: (value: Date) => {
                const d = dayjs(value).locale(i18n.language);
                if (diffDays > 180) {
                  return d.month() === 0 ? d.format("MMM YY") : d.format("MMM");
                }
                if (diffDays > 14) return d.format("DD MMM");
                if (diffDays > 1) {
                  return d.hour() === 0 ? d.format("DD MMM") : d.format("HH:mm");
                }
                return d.hour() === 0 && d.minute() === 0 ? d.format("DD MMM HH:mm") : d.format("HH:mm");
              },
            }]}
            yAxis={[{
              min: singleRecord ? singleValue - 1 : undefined,
              max: singleRecord ? singleValue + 1 : undefined,
              valueFormatter: (value: number) => (variableUnit ? `${value} ${variableUnit}` : `${value}`),
            }]}
            series={[{
              dataKey: "value",
              label: label,
              color: theme.palette.secondary.main,
              showMark: singleRecord,
            }]}
            slots={{
              axisContent: MonitoringAxisContent,
              noDataOverlay: NoDataOverlay,
            }}
            slotProps={{
              axisContent: {
                filteredData,
                variableUnit,
              } as MonitoringAxisContentProps,
              noDataOverlay: {
                message: t("captures.capture.monitoring.no-data"),
              } as NoDataOverlayProps,
            }}
            grid={{ vertical: true, horizontal: true }}
            height={height}
            skipAnimation
          />
        ) : (
          <Box sx={{ 
            height, 
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center", 
            justifyContent: "center",
            opacity: 0.5,
            border: `1px dashed ${theme.palette.divider}`,
            borderRadius: 1
          }}>
            <ShowChart sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6">
              {t("captures.capture.monitoring.select-variable-to-view-chart")}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}
